import PaginationList from '@/views/AssetWalletDetail/components/PaginationList'
import styles from './index.module.scss'
import {
  AccountTag,
  AutoSweepAddressBookListModel,
  AutoSweepAddressBookModel,
} from '@/services/apis/policy/model'
import { Button, Drawer, Input, Radio, Tabs, Tag, message } from 'antd'
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import { ReactComponent as CloseModalIcon } from '@/assets/icons/closeModal.svg'
import { ReactComponent as ArrowLeftIcon } from '@/assets/icons/arrowLeft.svg'
import { SweepAccount, SweepAccountPagination, SweepWallet } from '@/services/apis/sweep/models'
import { useFindSweepAccounts, useGetSweepWhiteList } from '@/hooks/sweep'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from '@/hooks/screen'
import { DestinationTypeEnum } from '@/constants/enum'
import AccountTypeTag from '@/components/AccountTypeTag'
import sweep from '@/services/apis/sweep'
const { Search } = Input

const useListHeight = (list: HTMLDivElement, totalPages: number) => {
  const windowSize = useWindowSize()
  const [height, setHeight] = useState(0)
  useEffect(() => {
    const { y = 0 } = list?.getBoundingClientRect?.() ?? {}
    const FOOTER_HEIGHT = totalPages > 1 ? 135 : 100
    setHeight(windowSize[1] - y - FOOTER_HEIGHT)
  }, [windowSize[1], totalPages])
  return height
}

interface PanelProps {
  coinKey?: string
  isGasStation: boolean
  onClose: () => void
  onNext: (account: SweepAccount | AutoSweepAddressBookModel) => void
  blockchainTypeKey?: string
  curWallet?: {
    address: string
    type: string
  } | null
}

const NativeAddress = memo<{
  account: SweepAccount
  onCreate: (account: SweepAccount, refresh: boolean) => Promise<boolean>
}>(({ account, onCreate }) => {
  const { t } = useTranslation()
  return account.nativeAddress ? (
    <div className={styles.addressText}> {account.nativeAddress}</div>
  ) : (
    <Button
      type="link"
      onClick={evt => {
        evt.stopPropagation()
        onCreate(account, true)
      }}
    >
      {t('autoSweep.createWalletAddress')}
    </Button>
  )
})

const AssetWalletPanel: FC<PanelProps> = ({
  coinKey,
  isGasStation,
  onClose,
  onNext,
  curWallet,
}) => {
  const { t } = useTranslation()
  const [searchVal, setSearchVal] = useState('')
  const ref = useRef<any>(null)
  const { data, isLoading, setCurrentPage, currentPage, setData } = useFindSweepAccounts(
    searchVal,
    coinKey
  )
  const height = useListHeight(ref.current, data.totalPages)

  const [selectedWallet, setSelectedWallet] = useState<SweepAccount>()

  const onSearch = (val: string) => {
    setSelectedWallet(undefined)
    setSearchVal(val)
  }

  const onCreate = useCallback(
    async (account: SweepAccount, refresh = false) => {
      const res = await sweep.createAddress({
        coinKey: coinKey!,
        accountKey: account.accountKey,
      })
      if (res.success) {
        if (refresh) {
          const idx = data.content.findIndex(item => item.accountKey === account.accountKey)
          data.content[idx] = {
            ...data.content[idx],
            nativeAddress: res.data.nativeAddress,
          }
          setData({ ...data })
        } else {
          account.nativeAddress = res.data.nativeAddress
        }
        return true
      }
      return false
    },
    [data.content, coinKey]
  )

  const onSelect = async (account: SweepAccount) => {
    if (!account.nativeAddress) {
      const result = await onCreate(account)
      if (result) {
        setSelectedWallet(account)
      }
      return
    }

    setSelectedWallet(account)
  }
  const handleData = (data: SweepAccountPagination) => {
    if (curWallet !== null) {
      let selectIndex = data.content?.findIndex(item => {
        return item?.nativeAddress === curWallet?.address
      })

      if (selectIndex > 0) {
        ;[data.content[0], data.content[selectIndex]] = [data.content[selectIndex], data.content[0]]
      }

      return data
    } else {
      return data
    }
  }

  return (
    <>
      <Search
        onSearch={onSearch}
        placeholder={t('autoSweep.enterWalletAddress') || ''}
        enterButton={<Button type="primary">{t('common.search')}</Button>}
        className={styles.search}
        allowClear
      />
      <div style={{ height, overflow: 'auto' }} ref={ref} className={styles.container}>
        <PaginationList
          data={handleData(data)}
          loading={isLoading}
          current={currentPage}
          onChange={setCurrentPage}
          renderItem={item => {
            const account = item as SweepAccount
            return (
              <div style={{ marginBottom: '15px' }} key={account.accountKey}>
                <Button className={styles.cardBtn} onClick={() => onSelect(account)}>
                  <div className={styles.cardText}>
                    <div className={styles.left}>
                      <div className={styles.name}>
                        {account.accountName}
                        <AccountTypeTag type={account.accountType} />
                      </div>
                      <NativeAddress account={account} onCreate={onCreate} />
                      {isGasStation && account.nativeAddress && (
                        <div className={styles.name}>
                          {t('common.balance')}:{`${account.nativeBalance} ${account.nativeSymbol}`}
                        </div>
                      )}
                    </div>
                    <Radio
                      checked={
                        selectedWallet?.accountKey
                          ? selectedWallet?.accountKey === account.accountKey
                          : account.nativeAddress === curWallet?.address
                      }
                    />
                  </div>
                </Button>
              </div>
            )
          }}
          isShowShadow={false}
        />
      </div>
      <div className={styles.footer}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="primary" onClick={() => onNext(selectedWallet!)} disabled={!selectedWallet}>
          {t('common.next')}
        </Button>
      </div>
    </>
  )
}

const WhiteListPanel: FC<PanelProps> = ({ blockchainTypeKey, onClose, onNext, curWallet }) => {
  const { t } = useTranslation()
  const [searchVal, setSearchVal] = useState('')
  const ref = useRef<any>(null)
  const { data, isLoading, setCurrentPage, currentPage } = useGetSweepWhiteList(
    searchVal,
    blockchainTypeKey
  )
  const height = useListHeight(ref.current, data.totalPages)

  const [selectedWhiteAddress, setSelectedWhiteAddress] = useState<AutoSweepAddressBookModel>()

  const onSearch = (val: string) => {
    setSelectedWhiteAddress(undefined)
    setSearchVal(val)
  }
  const handleData = (data: AutoSweepAddressBookListModel) => {
    if (curWallet !== null) {
      let selectIndex = data.content?.findIndex(item => {
        return item?.address === curWallet?.address
      })

      if (selectIndex > 0) {
        ;[data.content[0], data.content[selectIndex]] = [data.content[selectIndex], data.content[0]]
      }

      return data
    } else {
      return data
    }
  }
  return (
    <>
      <Search
        onSearch={onSearch}
        placeholder={t('wallet.sendDrawer.tab2.placeholder') || ''}
        enterButton={<Button type="primary">{t('common.search')}</Button>}
        className={styles.search}
        allowClear
      />
      <div style={{ height, overflow: 'auto' }} ref={ref}>
        <PaginationList
          data={handleData(data)}
          renderItem={item => {
            const account = item as AutoSweepAddressBookModel
            return (
              <div style={{ marginBottom: '15px' }} key={account.addressBookKey}>
                <Button
                  className={styles.cardBtn}
                  onClick={() => setSelectedWhiteAddress(account)}
                  disabled={account.accountTag === AccountTag.DEPOSIT}
                >
                  <div className={styles.cardText}>
                    <div className={styles.left}>
                      <div className={styles.name}>
                        {account.addressName}
                        {account.blockchainTypeName && <Tag>{account.blockchainTypeName}</Tag>}
                      </div>
                      <div className={styles.addressText}> {account.address}</div>
                      {account.accountTag === AccountTag.DEPOSIT && (
                        <div className={styles.depWalletText}>{t('autoSweep.depositWallet')}</div>
                      )}
                    </div>
                    <Radio
                      checked={
                        selectedWhiteAddress?.addressBookKey
                          ? account.addressBookKey === selectedWhiteAddress?.addressBookKey
                          : account.address === curWallet?.address
                      }
                    ></Radio>
                  </div>
                </Button>
              </div>
            )
          }}
          emptyText={t('autoSweep.emptyWhite')!}
          loading={isLoading}
          current={currentPage}
          onChange={setCurrentPage}
          isShowShadow={false}
        />
      </div>
      <div className={styles.footer}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          type="primary"
          onClick={() => onNext(selectedWhiteAddress!)}
          disabled={!selectedWhiteAddress}
        >
          {t('common.next')}
        </Button>
      </div>
    </>
  )
}

const DestWalletSelector: FC<PanelProps> = props => {
  const { t } = useTranslation()

  const items = [
    {
      label: t('wallet.sendDrawer.tab1.name'),
      key: 'assetWallet',
      children: <AssetWalletPanel {...props} />,
    },
    {
      label: t('wallet.sendDrawer.tab2.name'),
      key: 'whitelist',
      children: <WhiteListPanel {...props} />,
    },
  ]
  return (
    <Tabs
      defaultActiveKey={
        props.curWallet?.type === DestinationTypeEnum.INTERNAL_WALLET ? 'whitelist' : 'assetWallet'
      }
      items={items}
    />
  )
}

const GasWalletSelector: FC<PanelProps> = props => {
  return <AssetWalletPanel {...props} />
}

interface Props {
  open: boolean
  onClose: (e?: any) => void
  isGasStation?: boolean
  selectWallet: (wallet: SweepWallet, isGas: boolean) => void
  coinKey?: string
  blockchainTypeKey?: string
  curWallet: {
    address: string
    type: string
  } | null
}

const WalletDrawer: FC<Props> = props => {
  const {
    open,
    onClose,
    isGasStation = false,
    selectWallet,
    coinKey,
    blockchainTypeKey,
    curWallet,
  } = props
  const { t } = useTranslation()
  const commonPanelProps = {
    onClose,
    coinKey,
    isGasStation,
    blockchainTypeKey,
    curWallet,
  }
  const handleNext = (account: SweepAccount | AutoSweepAddressBookModel, isGas: boolean) => {
    let wallet: SweepWallet
    if ('addressBookKey' in account) {
      wallet = {
        blockchainTypeName: account.blockchainTypeName,
        accountName: account.addressName,
        nativeAddress: account.address,
        id: account.addressBookKey,
        destType: DestinationTypeEnum.INTERNAL_WALLET,
      }
    } else {
      wallet = {
        accountType: account.accountType,
        accountName: account.accountName,
        nativeAddress: account.nativeAddress,
        id: account.accountKey,
        destType: DestinationTypeEnum.VAULT_ACCOUNT,
        nativeBalance: account?.nativeBalance,
        nativeSymbol: account?.nativeSymbol,
      }
    }
    if (!wallet.nativeAddress) {
      message.error(t('autoSweep.emptyAddressWallet'))
      return
    }
    selectWallet(wallet, isGas)
    onClose()
  }
  return (
    <Drawer
      title={isGasStation ? t('autoSweep.gasStationAddress') : t('autoSweep.setWalletAddress')}
      placement="right"
      open={open}
      closeIcon={<ArrowLeftIcon onClick={onClose} />}
      push={false}
      onClose={onClose}
      destroyOnClose={true}
      width={560}
      extra={
        <div onClick={onClose} className={styles.closeIcon}>
          <CloseModalIcon />
        </div>
      }
      className={styles.container}
      mask={false}
    >
      {isGasStation ? (
        <GasWalletSelector
          {...commonPanelProps}
          onNext={(account: SweepAccount | AutoSweepAddressBookModel) => {
            handleNext(account, true)
          }}
        />
      ) : (
        <DestWalletSelector
          {...commonPanelProps}
          onNext={(account: SweepAccount | AutoSweepAddressBookModel) => {
            handleNext(account, false)
          }}
        />
      )}
    </Drawer>
  )
}

export default memo(WalletDrawer)
